import {
  deleteVehicleBrand,
  editVehicleBrand,
  addVehicleBrand,
} from "@/app/lib/actions/vehicle-brands";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useVehicleBrandsQuery,
  invalidateVehicleBrandsQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addVehicleBrandSchema,
  editVehicleBrandSchema,
} from "@/lib/schemas/vehicle-brands";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { toast } from "sonner";

export type VehicleBrand = {
  id: number;
  name: string;
  isActive: boolean;
};

function VehicleBrandItemsListCard() {
  const queryClient = useQueryClient();

  const vehicleBrandsQuery = useVehicleBrandsQuery({ sort: { name: "asc" } });
  const handleCreate = async (
    vehicleBrandName: z.infer<typeof addVehicleBrandSchema>,
  ) => {
    const response = await addVehicleBrand(vehicleBrandName);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouvelle marque de véhicule créée.");
      invalidateVehicleBrandsQuery(queryClient);
    }
  };

  const handleUpdate = async (
    vehicleBrand: z.infer<typeof editVehicleBrandSchema>,
  ) => {
    const response = await editVehicleBrand(vehicleBrand);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Marque de véhicule modifiée.");
      invalidateVehicleBrandsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteVehicleBrand(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Marque de véhicule suppriméé.");
      invalidateVehicleBrandsQuery(queryClient);
    }
  };

  if (vehicleBrandsQuery.isPending) return <ItemsListCardSkeleton />;
  const vehicleBrands = vehicleBrandsQuery.data?.vehicleBrands ?? [];

  return (
    <ItemsListCard<VehicleBrand>
      title="Marques de véhicule"
      items={vehicleBrands}
      createSchema={addVehicleBrandSchema}
      editSchema={editVehicleBrandSchema}
      defaultCreateValues={{ name: "", isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { VehicleBrandItemsListCard };
